import { Injectable } from '@angular/core';
import { AccessControlConfigService } from './roleAccessControlConfig.service';
import { CreditsManagersService } from '../services/creditsManagers.service';

/**
 * @fileOverview Subscription-based policy enforcement and feature access resolution
 * @description Manages subscription validation, policy filtering, and feature accessibility
 * resolution based on active subscriptions and user roles. Bridges the gap between subscription
 * data and feature accessibility by implementing subscription-based feature unlocking logic.
 * 
 * @module policyChecker
 */

/**
 * @service PolicyCheckerService
 * @description Service that manages and enforces subscription-based policy access control.
 * Orchestrates the initialization and management of user subscriptions, filtering them based
 * on role and subscription constraints, and resolving accessible features based on active
 * subscription plans.
 *
 * Core Responsibilities:
 * - Initialize and validate active subscriptions for the logged-in user
 * - Filter subscriptions based on role, business type, and licensing constraints
 * - Resolve accessible features combining default role features with subscription-unlocked features
 * - Persist active subscriptions and accessible features in localStorage for app-wide access
 * - Handle special cases (BluEye licensing, employee/provider business subscriptions)
 * 
 * Integration Points:
 * - AccessControlConfigService: Provides role-feature and policy-feature mappings
 * - CreditsManagersService: Fetches subscription data from backend
 * 
 * Data Persistence:
 * - 'Policies': All subscriptions fetched from backend
 * - 'Active_Policy_Details': Filtered, role-accessible, non-expired subscriptions
 * - 'Accessible_Features': Complete feature set available to current user
 * 
 * Architecture:
 * - Subscription initialization on app startup
 * - Role-based subscription filtering (prevents unauthorized plan access)
 * - Expiration checking (removes expired subscriptions)
 * - Policy flag extraction for feature lookups
 * - Feature merging (default + subscription-unlocked)
 * 
 * @example
 * // In AppComponent or similar initialization
 * constructor(private policyChecker: PolicyCheckerService) {}
 * 
 * async ngOnInit() {
 *   try {
 *     // Initialize subscriptions and resolve features
 *     const features = await this.policyChecker.initializeSubscriptions();
 *     console.log('User can access features:', features);
 *     
 *     // Later, retrieve already-resolved features
 *     const currentFeatures = this.policyChecker.getUserAccessibleFeatures();
 *   } catch (error) {
 *     console.error('Failed to initialize subscriptions:', error);
 *   }
 * }
 * 
 * @example
 * // In a feature check component
 * constructor(private policyChecker: PolicyCheckerService) {}
 * 
 * canAccessAnalytics(): boolean {
 *   const features = this.policyChecker.getUserAccessibleFeatures();
 *   return features.includes('ADVANCED_ANALYTICS');
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyCheckerService {
  /**
   * @constructor
   * @description Initializes the PolicyCheckerService with dependencies.
   * 
   * @param {CreditsManagersService} creditsManagerService - Service for fetching subscription data from backend
   * @param {AccessControlConfigService} accessControlConfigService - Service for role-feature mappings
   */
  constructor(
    private creditsManagerService: CreditsManagersService,
    private accessControlConfigService: AccessControlConfigService,
  ) {}

  /**
   * @method initializeSubscriptions
   * @async
   * @description Initializes the logged-in user's active subscriptions and resolves their accessible features.
   * This is the primary entry point for subscription setup and should be called during application
   * initialization. Handles special cases for employee and provider business roles that don't
   * use subscription-based access control.
   *
   * Initialization Flow (6 Steps):
   * 1. **Check Role Eligibility**: Skip subscription processing for EMPLOYEE role, Provider business,
   *    or Buyer/SME business types (they have role-based access only)
   * 2. **Fetch All Subscriptions**: Retrieve all subscriptions for the current business from backend
   * 3. **Persist All Subscriptions**: Store complete subscription list in localStorage['Policies']
   * 4. **Filter Active**: Keep only non-expired subscriptions (isExpired === false)
   * 5. **Filter by Role**: Apply role-specific access rules (e.g., BluEye licensing validation)
   * 6. **Extract & Store Policy Details**: Extract policyDetails and save in localStorage['Active_Policy_Details']
   * 7. **Resolve Features**: Calculate accessible features combining default + subscription-unlocked
   * 
   * Special Cases:
   * - **EMPLOYEE Role**: Cannot have subscriptions; features based on role only; clears policies
   * - **Provider Business**: Subscription-less access; role determines available features
   * - **BluEye Plans**: Require device license check for specific roles
   * 
   * Storage Persistence:
   * - localStorage['Policies']: All fetched subscriptions (raw data)
   * - localStorage['Active_Policy_Details']: Filtered, accessible subscriptions
   * - localStorage['Accessible_Features']: Final resolved feature list (updated by resolveUserFeatures)
   * 
   * @public
   * @async
   * @returns {Promise<string[]>} Array of accessible feature IDs after initialization.
   *   Features are combination of default role features + subscription-unlocked features.
   *   Returns empty array if no subscriptions are active.
   * 
   * @throws {Error} If subscription fetching or processing fails (errors are caught internally)
   * 
   * @example
   * // In AppComponent initialization
   * async ngOnInit() {
   *   try {
   *     const accessibleFeatures = await this.policyChecker.initializeSubscriptions();
   *     console.log('User can access:', accessibleFeatures);
   *     // accessibleFeatures: ['DASHBOARD', 'POND', 'FEED', 'ADVANCED_ANALYTICS']
   *   } catch (error) {
   *     console.error('Subscription initialization failed:', error);
   *     // Handle error gracefully
   *   }
   * }
   * 
   * @example
   * // Special case: Employee role
   * // localStorage['loginPersonType'] = 'EMPLOYEE'
   * // Result: Subscriptions cleared, features based on role only
   * const features = await this.policyChecker.initializeSubscriptions();
   * // features: ['ROUTES', 'FEATURES', 'SCREEN', 'SYNC', ...all super admin features]
   */
  public async initializeSubscriptions(): Promise<string[]> {
    try {
      const role = localStorage.getItem('loginPersonType');
      const businessCategory = localStorage.getItem('loginBusinessCategory');
      const businessType = localStorage.getItem('loginBusinessType')

      this.clearSubscriptions();
      return this.resolveUserFeatures();
    } catch (error) {
      
    }
  }

  /* ===================== PRIVATE HELPER METHODS ===================== */

  /**
   * @method clearSubscriptions
   * @description Clears all active policy details from localStorage. Used when subscription-based
   * access control doesn't apply (EMPLOYEE, Provider business, Buyer/SME types). Sets the
   * 'Active_Policy_Details' to an empty array, indicating no subscription features are available.
   * 
   * @private
   * @returns {void}
   */
  private clearSubscriptions(): void {
    localStorage.setItem('Active_Policy_Details', JSON.stringify([]));
  }

  /**
   * @method fetchUserSubscriptions
   * @async
   * @description Fetches all subscriptions for the current logged-in business from the backend.
   * Makes an HTTP call to retrieve complete subscription data including policyDetails,
   * devicesDetails, and other subscription metadata.
   * 
   * @private
   * @async
   * @returns {Promise<any[]>} Promise resolving to array of subscription objects.
   *   Each subscription contains:
   *   - policyDetails: { name, policyFlag, description }
   *   - devicesDetails: Array of device configurations (for BluEye plans)
   *   - isExpired: Boolean indicating subscription validity
   *   - Other metadata
   * 
   * @example
   * // Called internally during initialization
   * const allSubscriptions = await this.fetchUserSubscriptions();
   * // Returns: [
   * //   { policyDetails: { name: 'LITE', policyFlag: 'LITE' }, isExpired: false },
   * //   { policyDetails: { name: 'BluEye Launch Plan' }, isExpired: false, devicesDetails: [...] }
   * // ]
   */
  private fetchUserSubscriptions(): Promise<any[]> {
    // Fetch all active subscriptions for the current business
    return this.creditsManagerService.getSubscriptionsByBusinessID();
  }

  /**
   * @method resolveActiveSubscriptions
   * @description Filters a subscription array to keep only non-expired subscriptions.
   * Checks the isExpired flag on each subscription and removes any that have expired.
   * 
   * @private
   * @param {any[]} subscriptions - Array of subscription objects to filter
   * @returns {any[]} Filtered array containing only subscriptions where isExpired === false.
   *   Returns empty array if input is null/undefined.
   * 
   * @example
   * const allSubs = [
   *   { policyDetails: { name: 'LITE' }, isExpired: false },
   *   { policyDetails: { name: 'CORE' }, isExpired: true }
   * ];
   * const active = this.resolveActiveSubscriptions(allSubs);
   * // Returns: [{ policyDetails: { name: 'LITE' }, isExpired: false }]
   */
  private resolveActiveSubscriptions(subscriptions: any[]): any[] {
    return subscriptions?.filter(sub => !sub.isExpired) ?? [];
  }

  /**
   * @method storeActivePolicyDetails
   * @description Extracts the policyDetails property from each subscription and persists them
   * to localStorage under the key 'Active_Policy_Details'. This is used later to extract
   * policy flags for feature lookups.
   * 
   * Data Structure Stored:
   * ```typescript
   * localStorage['Active_Policy_Details'] = [
   *   { name: 'LITE', policyFlag: 'LITE', description: '...' },
   *   { name: 'CORE', policyFlag: 'CORE', description: '...' }
   * ]
   * ```
   * 
   * @private
   * @param {any[]} subscriptions - Array of subscription objects with policyDetails
   * @returns {void}
   * 
   * @example
   * const activeSubs = [
   *   { policyDetails: { name: 'LITE', policyFlag: 'LITE' }, ... },
   *   { policyDetails: { name: 'CORE', policyFlag: 'CORE' }, ... }
   * ];
   * this.storeActivePolicyDetails(activeSubs);
   * // Stores in localStorage: '[{ name: "LITE", policyFlag: "LITE" }, ...]'
   */
  private storeActivePolicyDetails(subscriptions: any[]): void {
    const policyDetails = subscriptions.map(sub => sub.policyDetails);
    localStorage.setItem('Active_Policy_Details', JSON.stringify(policyDetails));
  }

  /**
   * Retrieves the user's active policy details from localStorage.
   *
   * @returns {any[]} Array of active subscription policy objects.
   */
  public getUserActiveSubscriptions(): any[] {
    return JSON.parse(localStorage.getItem('Active_Policy_Details') ?? '[]');
  }

  /**
   * Resolves all accessible features for the currently logged-in user.
   *
   * @returns {string[]} Array of accessible feature IDs.
   */
  public resolveUserFeatures(): string[] {
    // Get logged-in user details from localStorage
    const role = localStorage.getItem('loginPersonType') || '';
    const businessCategory = localStorage.getItem('loginBusinessCategory') || '';

    /**
     * Updated role label
     * - If user is a Business Admin:
     *    → Show "Provider Business Admin" when businessType = Provider
     *    → Show "Farm Business Admin" when businessType = Farm
     * - Otherwise: keep the original role
     */
    const updatedRole =
      role === 'Business Admin'
        ? `${businessCategory} Business Admin`
        : role;

    const activePolicyFlags = this.extractActivePolicyFlags();

    const defaultFeatures = this.resolveDefaultFeatures(updatedRole);
    const subscribedFeatures = this.resolveSubscriptionUnlockedFeatures(
      updatedRole,
      activePolicyFlags
    );

    localStorage.setItem(
      'Accessible_Features',
      JSON.stringify([...defaultFeatures, ...subscribedFeatures])
    );

    return [...defaultFeatures, ...subscribedFeatures];
  }

  /**
   * Extracts policy flags from active subscriptions.
   *
   * @returns {string[]} Array of policy flag strings.
   */
  private extractActivePolicyFlags(): string[] {
    return this.getUserActiveSubscriptions().map(sub => sub.policyFlag);
  }

  /**
   * Returns the default features always accessible for a given role.
   *
   * @param {string | null} role - Role of the user.
   * @returns {string[]} Array of default feature IDs.
   */
  private resolveDefaultFeatures(role: string | null): string[] {
    return Array.from(this.accessControlConfigService.getDefaultFeaturesForRole(role ?? ''));
  }

  /**
   * Returns subscription-unlocked features allowed for a given role.
   *
   * @param {string | null} role - Role of the user.
   * @param {string[]} activePolicyFlags - Currently active policy flags.
   * @returns {string[]} Array of subscription-unlocked feature IDs.
   */
  private resolveSubscriptionUnlockedFeatures(role: string | null, activePolicyFlags: string[]): string[] {
    const featuresFromPolicies = this.accessControlConfigService.getFeaturesByActivePolicyFlags(activePolicyFlags);
    const lockableFeaturesForRole = Array.from(
      this.accessControlConfigService.getSubscriptionAllowedFeaturesForRole(role ?? '')
    );

    return lockableFeaturesForRole.filter(feature => featuresFromPolicies.includes(feature));
  }

  /**
   * Retrieves precomputed accessible features from localStorage.
   *
   * @returns {string[]} Array of accessible feature IDs.
   */
  public getUserAccessibleFeatures(): string[] {
    return JSON.parse(localStorage.getItem('Accessible_Features') ?? '[]');
  }
}
